import React from "react";
import { Col, Form, Row } from "react-bootstrap";
import { NumericFormat } from "react-number-format";
import { FaCheck, FaTimes } from "react-icons/fa";
import type {
  AssociadoAnaliseItem,
  ReceitaOperacionalAnaliseItem,
} from "../../../../../../services";
import type { SomenteAnalistaVariant } from "../../../../../../domain/shipping/models";
import "./AnalistaIndicadoresSecao.css";

function fmtBRL(n: number): string {
  return Number(n).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function pctEvolucao(anterior: number, atual: number): string {
  if (!Number.isFinite(anterior) || anterior === 0) return "0%";
  const p = ((atual - anterior) / anterior) * 100;
  return `${p.toLocaleString("pt-BR", {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  })}%`;
}

function ultimosDoisAnos<T extends { anoReferencia: number }>(
  itens: T[],
): { anterior: T | null; atual: T | null } {
  const sorted = [...itens].sort(
    (a, b) => a.anoReferencia - b.anoReferencia,
  );
  if (sorted.length === 0) return { anterior: null, atual: null };
  if (sorted.length === 1) return { anterior: null, atual: sorted[0] };
  return {
    anterior: sorted[sorted.length - 2],
    atual: sorted[sorted.length - 1],
  };
}

function deduplicarPorAno<T extends { anoReferencia: number }>(itens: T[]): T[] {
  const byAno = new Map<number, T>();
  for (const item of itens) {
    byAno.set(item.anoReferencia, item);
  }
  return Array.from(byAno.values()).sort((a, b) => a.anoReferencia - b.anoReferencia);
}

function resolveAnoRange(
  anterior: number | null | undefined,
  atual: number | null | undefined,
): { anoAnteriorLabel: number; anoAtualLabel: number } {
  if (
    typeof anterior === "number" &&
    Number.isFinite(anterior) &&
    typeof atual === "number" &&
    Number.isFinite(atual)
  ) {
    return { anoAnteriorLabel: anterior, anoAtualLabel: atual };
  }

  // Quando só há um ponto, exibimos "ano" e "ano+1"
  // para manter a leitura de evolução no ciclo atual.
  if (typeof atual === "number" && Number.isFinite(atual)) {
    return { anoAnteriorLabel: atual, anoAtualLabel: atual + 1 };
  }
  if (typeof anterior === "number" && Number.isFinite(anterior)) {
    return { anoAnteriorLabel: anterior, anoAtualLabel: anterior + 1 };
  }

  const anoAtual = new Date().getFullYear();
  return { anoAnteriorLabel: anoAtual - 1, anoAtualLabel: anoAtual };
}

export interface AnalistaIndicadoresSecaoProps {
  variant: SomenteAnalistaVariant;
  receitasOperacionais: ReceitaOperacionalAnaliseItem[];
  associados: AssociadoAnaliseItem[];
  
  salarioMinimoAnoAnterior?: number | null;
  
  receitaOperacionalReferencia?: number | null;
  
  associadosReferencia?: number | null;
  
  onReceitaOperacionalReferenciaChange?: (value: number) => void;
  
  onAssociadosReferenciaChange?: (value: number) => void;
}

const AnalistaIndicadoresSecao: React.FC<AnalistaIndicadoresSecaoProps> = ({
  variant,
  receitasOperacionais,
  associados,
  salarioMinimoAnoAnterior = null,
  receitaOperacionalReferencia = null,
  associadosReferencia = null,
  onReceitaOperacionalReferenciaChange,
  onAssociadosReferenciaChange,
}) => {
  if (variant === "receita-arrecadacao") {
    const sorted = [...receitasOperacionais].sort(
      (a, b) => b.anoReferencia - a.anoReferencia,
    );
    const maisRecente = sorted[0];
    const receitaReferenciaAnalista =
      receitaOperacionalReferencia != null &&
      Number.isFinite(Number(receitaOperacionalReferencia))
        ? Number(receitaOperacionalReferencia)
        : NaN;
    const receitaVal = Number.isFinite(receitaReferenciaAnalista)
      ? receitaReferenciaAnalista
      : maisRecente
        ? Number(maisRecente.valor)
        : NaN;
    const sm =
      salarioMinimoAnoAnterior != null &&
      Number.isFinite(Number(salarioMinimoAnoAnterior))
        ? Number(salarioMinimoAnoAnterior)
        : NaN;
    const dozeSm = Number.isFinite(sm) ? sm * 12 : NaN;
    const podeComparar = Number.isFinite(receitaVal) && Number.isFinite(dozeSm);
    const roMaiorQueDozeSm = podeComparar && receitaVal > dozeSm;

    return (
      <div className="analista-indicadores-secao">
        <Row className="g-3 align-items-end">
          <Col md={3} sm={6}>
            <Form.Group>
              <Form.Label className="fw-bold small mb-1">
                Receita operacional
              </Form.Label>
              <Form.Control
                type="text"
                readOnly
                className="bg-light"
                value={
                  Number.isFinite(receitaVal)
                    ? fmtBRL(receitaVal)
                    : "—"
                }
              />
            </Form.Group>
          </Col>
          <Col md={3} sm={6}>
            <Form.Group>
              <Form.Label className="fw-bold small mb-1">
                Salário mín. ano anterior
              </Form.Label>
              <Form.Control
                type="text"
                readOnly
                className="bg-light"
                value={Number.isFinite(sm) ? fmtBRL(sm) : "—"}
              />
            </Form.Group>
          </Col>
          <Col md={3} sm={6}>
            <Form.Group>
              <Form.Label className="fw-bold small mb-1">
                12 salários mínimo
              </Form.Label>
              <Form.Control
                type="text"
                readOnly
                className="bg-light"
                value={Number.isFinite(dozeSm) ? fmtBRL(dozeSm) : "—"}
              />
            </Form.Group>
          </Col>
          <Col md={3} sm={6}>
            <Form.Group>
              <Form.Label className="fw-bold small mb-1">
                RO &gt; 12 salários mín
              </Form.Label>
              <div className="analista-indicadores-ro-toggle d-flex gap-2">
                <button
                  type="button"
                  disabled
                  className={
                    roMaiorQueDozeSm
                      ? "analista-indicadores-ro-toggle__btn analista-indicadores-ro-toggle__btn--sim analista-indicadores-ro-toggle__btn--active"
                      : "analista-indicadores-ro-toggle__btn analista-indicadores-ro-toggle__btn--sim"
                  }
                  aria-pressed={podeComparar ? roMaiorQueDozeSm : undefined}
                >
                  <FaCheck className="me-1" aria-hidden />
                  SIM
                </button>
                <button
                  type="button"
                  disabled
                  className={
                    podeComparar && !roMaiorQueDozeSm
                      ? "analista-indicadores-ro-toggle__btn analista-indicadores-ro-toggle__btn--nao analista-indicadores-ro-toggle__btn--active"
                      : "analista-indicadores-ro-toggle__btn analista-indicadores-ro-toggle__btn--nao"
                  }
                  aria-pressed={
                    podeComparar ? !roMaiorQueDozeSm : undefined
                  }
                >
                  <FaTimes className="me-1" aria-hidden />
                  NÃO
                </button>
              </div>
            </Form.Group>
          </Col>
        </Row>
        {receitasOperacionais.length > 1 ? (
          <p className="text-muted small mt-2 mb-0">
            Há outros anos em &quot;Evolução receita operacional&quot;.
          </p>
        ) : null}
      </div>
    );
  }

  if (variant === "evolucao-receita") {
    const serie = deduplicarPorAno(receitasOperacionais);
    const { anterior, atual } = ultimosDoisAnos(serie);
    const valorAtualExibicao =
      receitaOperacionalReferencia != null &&
      Number.isFinite(Number(receitaOperacionalReferencia))
        ? Number(receitaOperacionalReferencia)
        : atual?.valor;
    const { anoAnteriorLabel, anoAtualLabel } = resolveAnoRange(
      anterior?.anoReferencia,
      atual?.anoReferencia,
    );
    return (
      <div className="analista-indicadores-secao">
        <Row className="g-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label className="fw-bold small mb-1">
                {anoAnteriorLabel}
              </Form.Label>
              <Form.Control
                type="text"
                readOnly
                className="bg-light"
                value={anterior ? fmtBRL(anterior.valor) : "—"}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="fw-bold small mb-1">
                {anoAtualLabel}
              </Form.Label>
              {onReceitaOperacionalReferenciaChange ? (
                <NumericFormat
                  customInput={Form.Control}
                  value={
                    Number.isFinite(Number(valorAtualExibicao))
                      ? Number(valorAtualExibicao)
                      : ""
                  }
                  onValueChange={(values) => {
                    const v = values.floatValue;
                    onReceitaOperacionalReferenciaChange(
                      v === undefined ? 0 : v,
                    );
                  }}
                  thousandSeparator="."
                  decimalSeparator=","
                  prefix="R$ "
                  allowNegative={false}
                  decimalScale={2}
                  fixedDecimalScale
                  placeholder="R$ 0,00"
                />
              ) : (
                <Form.Control
                  type="text"
                  readOnly
                  className="bg-light"
                  value={
                    Number.isFinite(Number(valorAtualExibicao))
                      ? fmtBRL(Number(valorAtualExibicao))
                      : "—"
                  }
                />
              )}
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="fw-bold small mb-1">% evolução</Form.Label>
              <Form.Control
                type="text"
                readOnly
                className="bg-light"
                value={
                  anterior && Number.isFinite(Number(valorAtualExibicao))
                    ? pctEvolucao(anterior.valor, Number(valorAtualExibicao))
                    : "0%"
                }
              />
            </Form.Group>
          </Col>
        </Row>
      </div>
    );
  }

  const serieAssociados = deduplicarPorAno(associados);
  const { anterior, atual } = ultimosDoisAnos(serieAssociados);
  const associadosAtualExibicao =
    associadosReferencia != null && Number.isFinite(Number(associadosReferencia))
      ? Number(associadosReferencia)
      : atual?.quantidade;
  const { anoAnteriorLabel, anoAtualLabel } = resolveAnoRange(
    anterior?.anoReferencia,
    atual?.anoReferencia,
  );
  return (
    <div className="analista-indicadores-secao">
      <Row className="g-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label className="fw-bold small mb-1">
              {`novembro/${anoAnteriorLabel}`}
            </Form.Label>
            <Form.Control
              type="text"
              readOnly
              className="bg-light"
              value={
                anterior != null ? String(anterior.quantidade) : "—"
              }
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label className="fw-bold small mb-1">
              {`junho/${anoAtualLabel}`}
            </Form.Label>
            <Form.Control
              type={onAssociadosReferenciaChange ? "number" : "text"}
              readOnly={!onAssociadosReferenciaChange}
              className={!onAssociadosReferenciaChange ? "bg-light" : undefined}
              step="1"
              value={
                onAssociadosReferenciaChange
                  ? Number.isFinite(Number(associadosAtualExibicao))
                    ? String(associadosAtualExibicao)
                    : ""
                  : Number.isFinite(Number(associadosAtualExibicao))
                    ? String(associadosAtualExibicao)
                    : "—"
              }
              onChange={(e) => {
                if (!onAssociadosReferenciaChange) return;
                const parsed = Number(e.target.value);
                if (Number.isFinite(parsed)) {
                  onAssociadosReferenciaChange(parsed);
                }
              }}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label className="fw-bold small mb-1">% evolução</Form.Label>
            <Form.Control
              type="text"
              readOnly
              className="bg-light"
              value={
                  anterior && Number.isFinite(Number(associadosAtualExibicao))
                    ? pctEvolucao(anterior.quantidade, Number(associadosAtualExibicao))
                  : "0%"
              }
            />
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
};

export default AnalistaIndicadoresSecao;
